import React, { useMemo, lazy, Suspense } from 'react'
import { useAuth } from '@context/AuthContext/AuthContext'
import { BrowserRouter as Router } from 'react-router'
import { SkillsProvider } from '@context/SkillsContext/SkillsContext'
import { DonationsProvider } from '@context/DonationsContext/DonationsContext'
import { EventsProvider } from '@context/EventContext/EventContext'
import { UserProvider } from '@context/UserContext/UserContext'
import Loading from '@components/Common/Loading/Loading'
import ChunkLoadingFallback from '@components/Common/ChunkLoadingFallback/ChunkLoadingFallback'

const LazyAuthNavigator = lazy(() => import('@routers/AuthNavigator/AuthNavigator'))
const LazyOnboardingNavigator = lazy(() => import('@routers/OnboardingNavigator/OnboardingNavigator'))
const LazyOrganizationNavigator = lazy(() => import('@routers/OrganizationNavigator/OrganizationNavigator'))
const LazyInviteNavigator = lazy(() => import('@routers/InviteNavigator/InviteNavigator'))

const RootNavigator = (): React.JSX.Element => {
  const { currentUser, onboarding } = useAuth()

  // Get subdomain from hostname (memoized - hostname doesn't change during app lifecycle)
  const subdomain = useMemo(
    () => window.location.hostname.split('.').slice(0, -1).join('.'),
    []
  )

  // Memoize invite check
  const isInviteSubdomain = useMemo(
    () => subdomain.includes('invite'),
    [subdomain]
  )

  // Handle invite subdomain routing
  if (isInviteSubdomain) {
    return (
      <Router>
        <Suspense fallback={<ChunkLoadingFallback />}>
          <LazyInviteNavigator />
        </Suspense>
      </Router>
    )
  }

  return (
    <>
      <Router>
        <Suspense fallback={<ChunkLoadingFallback />}>
          {currentUser === null && !onboarding ? (
            <LazyAuthNavigator />
          ) : (
            <SkillsProvider>
              <DonationsProvider>
                <UserProvider>
                  <EventsProvider>
                    {onboarding ? (
                      <LazyOnboardingNavigator />
                    ) : (
                      <LazyOrganizationNavigator />
                    )}
                  </EventsProvider>
                </UserProvider>
              </DonationsProvider>
            </SkillsProvider>
          )}
        </Suspense>
      </Router>

      <Loading />
    </>
  )
}

export default RootNavigator
